import { createAction } from '@reduxjs/toolkit';
import {
  ContentLibraryFileActionPayloadBase,
  ContentLibraryFileUploadAcceptedPayload,
  ContentLibraryOpenedFile,
  ContentLibraryOpenedFilesPayload,
  ContentLibraryPageChangedPayload,
  ContentLibraryReceivedPayload,
  ContentLibrarySharedFile,
  ContentLibraryMediaPlayerRemotePayload,
  CreateContentLibraryEmbedPayload,
  CreateContentLibraryFolderPayload,
  DeleteContentLibraryItemPayload,
  EmbedContentCreatedPayload,
  EmptyContentLibraryReceivedPayload,
  FileReceivedPayload,
  FolderReceivedPayload,
  MoveContentLibraryItemBroadcastPayload,
  MoveContentLibraryItemPayload,
  RenameContentLibraryItemPayload,
  WhiteboardCreatedPayload,
} from 'features/content-library/types';
import { EntityIdPayload, SignalingEventPayload } from 'services/signaling';
import type { UserId } from 'features/users/types';

export const signalingLibraryReceived = createAction<
  ContentLibraryReceivedPayload | EmptyContentLibraryReceivedPayload
>('signaling/contentLibrary/libraryReceived');

export const createContentFolderRequested = createAction<CreateContentLibraryFolderPayload>(
  'contentLibrary/createFolderRequested'
);
export const signalingContentLibraryFolderCreated = createAction<FolderReceivedPayload>(
  'signaling/contentLibrary/folderCreated'
);

export const renameContentFolderRequested = createAction<RenameContentLibraryItemPayload>(
  'contentLibrary/renameFolderRequested'
);
export const signalingContentLibraryFolderRenamed = createAction<RenameContentLibraryItemPayload>(
  'signaling/contentLibrary/folderRenamed'
);

export const moveContentFolderRequested = createAction<MoveContentLibraryItemPayload>(
  'contentLibrary/moveFolderRequested'
);
export const signalingContentLibraryFolderMoved =
  createAction<MoveContentLibraryItemBroadcastPayload>('signaling/contentLibrary/folderMoved');

export const moveContentLibraryFileRequested = createAction<MoveContentLibraryItemPayload>(
  'contentLibrary/moveFileRequested'
);
export const signalingContentLibraryFileMoved =
  createAction<MoveContentLibraryItemBroadcastPayload>('signaling/contentLibrary/fileMoved');

export const deleteContentLibraryFolderRequested = createAction<DeleteContentLibraryItemPayload>(
  'contentLibrary/deleteFolderRequested'
);
export const signalingContentLibraryFolderDeleted = createAction<DeleteContentLibraryItemPayload>(
  'signaling/contentLibrary/folderDeleted'
);

export const renameContentLibraryFileRequested = createAction<RenameContentLibraryItemPayload>(
  'contentLibrary/renameFileRequested'
);
export const signalingContentLibraryFileRenamed = createAction<RenameContentLibraryItemPayload>(
  'signaling/contentLibrary/fileRenamed'
);

export const deleteContentLibraryFileRequested = createAction<DeleteContentLibraryItemPayload>(
  'contentLibrary/deleteFileRequested'
);
export const signalingContentLibraryFileDeleted = createAction<DeleteContentLibraryItemPayload>(
  'signaling/contentLibrary/fileDeleted'
);

export const cancelContentLibraryFileProcessingRequested =
  createAction<DeleteContentLibraryItemPayload>('contentLibrary/cancelFileProcessingRequested');

// file upload
export const signalingContentLibraryFileUploaded = createAction<FileReceivedPayload>(
  'signaling/contentLibrary/fileUploaded'
);
export const signalingContentLibraryFileUploadAccepted =
  createAction<ContentLibraryFileUploadAcceptedPayload>(
    'signaling/contentLibrary/fileUploadAccepted'
  );
export const contentLibraryFileUploadRequested = createAction<FileList | null>(
  'contentLibrary/fileUploadRequested'
);

// file conversion
export const signalingContentLibraryFileConverted =
  createAction<ContentLibraryFileActionPayloadBase>('signaling/contentLibrary/fileConverted');
export const signalingContentLibraryConversionFailed =
  createAction<ContentLibraryFileActionPayloadBase>('signaling/contentLibrary/conversionFailed');
export const signalingContentLibraryConversionProgress = createAction<
  ContentLibraryFileActionPayloadBase & {
    percentage: number;
  }
>('signaling/contentLibrary/conversionProgress');

// open file
export const contentLibraryFileOpenRequested = createAction<{
  id: string;
  personal?: boolean;
}>('contentLibrary/fileOpenRequested');

export const contentLibraryFilePreviewRequested = createAction<{
  id: string;
  personal?: boolean;
}>('contentLibrary/filePreviewRequested');
export const contentLibraryFilePreviewClosed = createAction('contentLibrary/filePreviewClosed');
export const signalingContentLibraryFileOpened = createAction<
  SignalingEventPayload<ContentLibraryOpenedFile>
>('signaling/contentLibrary/fileOpened');
export const signalingContentLibraryFileClosed = createAction<{ id: string }>(
  'signaling/contentLibrary/fileClosed'
);
export const bulkFilesClosed = createAction<string[]>('contentLibrary/bulkFilesClosed');
export const contentLibraryChangeFilePageRequested = createAction<{
  id: string;
  page: number;
}>('contentLibrary/fileChangePageRequested');
export const signalingContentLibraryFilePageChanged = createAction<
  SignalingEventPayload<ContentLibraryPageChangedPayload>
>('signaling/contentLibrary/filePageChanged');

export const signalingContentLibraryOpenedFilesReceived =
  createAction<ContentLibraryOpenedFilesPayload>('signaling/contentLibrary/openedFilesReceived');

// share files
export const contentLibraryFileShareRequested = createAction<{
  id: string;
  personal: boolean;
  targetUsers?: UserId[];
}>('contentLibrary/fileShareRequested');

export const contentLibraryFileUnshareRequested = createAction<EntityIdPayload>(
  'contentLibrary/fileUnshareRequested'
);
export const signalingContentLibrarySharedFilesReceived = createAction<{
  files: ContentLibrarySharedFile[];
}>('signaling/contentLibrary/sharedFilesReceived');

export const signalingContentLibraryFileShared = createAction<
  ContentLibrarySharedFile & {
    targetUsers?: UserId[];
  }
>('signaling/contentLibrary/fileShared');
export const signalingContentLibraryFileUnshared = createAction<EntityIdPayload>(
  'signaling/contentLibrary/fileUnshared'
);

//copy files
export const contentLibraryCopyFileRequested = createAction<ContentLibraryFileActionPayloadBase>(
  'contentLibrary/copyFileRequested'
);
export const signalingContentLibraryFileCopied = createAction<
  SignalingEventPayload<WhiteboardCreatedPayload | FileReceivedPayload | EmbedContentCreatedPayload>
>('signaling/contentLibrary/fileCopied');

// embed content
export const createEmbedContentRequested = createAction<CreateContentLibraryEmbedPayload>(
  'contentLibrary/createEmbedContentRequested'
);
export const signalingContentLibraryEmbedContentCreated = createAction<EmbedContentCreatedPayload>(
  'signaling/contentLibrary/embedContentCreated'
);
export const downloadContentLibraryFileRequested = createAction<{ id: string; personal?: boolean }>(
  'contentLibrary/downloadFileRequested'
);
export const signalingContentLibraryMediaPlayerStateChanged =
  createAction<ContentLibraryMediaPlayerRemotePayload>(
    'signaling/contentLibrary/mediaPlayerStateChanged'
  );

export const fileViewed = createAction<{ id: string; data?: Partial<ContentLibraryOpenedFile> }>(
  'contentLibrary/fileViewed'
);
export const whiteboardViewed = createAction<string>('contentLibrary/whiteboardViewed');
